import React, { useEffect, useState, useMemo } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtNum, exportToPDF, exportToExcel } from "../lib/exports";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { FileDown, FileSpreadsheet, Edit, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function HistorialVentas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const puedeEditar = canEdit(user, "historial_ventas");
  const [fechaIni, setFechaIni] = useState(monthStart());
  const [fechaFin, setFechaFin] = useState(today());
  const [sucursalId, setSucursalId] = useState("");
  const [productoId, setProductoId] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [productos, setProductos] = useState([]);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/sucursales"), api.get("/productos")]).then(([rs, rp]) => {
      setSucursales(rs.data); setProductos(rp.data);
    });
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = { fecha_ini: fechaIni, fecha_fin: fechaFin };
      if (sucursalId) params.sucursal_id = sucursalId;
      if (productoId) params.producto_id = productoId;
      const { data } = await api.get("/ventas", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []); // initial

  const sucName = (id) => sucursales.find((s) => s.id === id)?.nombre || "—";

  const totalVentas = useMemo(() => rows.reduce((s, r) => s + (r.total || 0), 0), [rows]);
  const totalUnid = useMemo(() => rows.reduce((s, r) => s + (r.unidades || 0), 0), [rows]);

  const expPDF = () => {
    const cols = [
      { header: "Fecha", key: "fecha" },
      { header: "Sucursal", key: "sucursal" },
      { header: "Usuario", key: "usuario_email" },
      { header: "Unidades", key: "unidades" },
      { header: "Total", key: "total_fmt" },
    ];
    const data = rows.map((r) => ({ ...r, sucursal: sucName(r.sucursal_id), total_fmt: fmtMoney(r.total) }));
    exportToPDF({
      title: "Historial de Ventas",
      subtitle: `${fechaIni} a ${fechaFin}`,
      columns: cols, rows: data,
      summary: [{ label: "Total General", value: fmtMoney(totalVentas) }, { label: "Tickets", value: rows.length }],
    });
  };
  const expXLS = () => {
    exportToExcel({
      title: "Historial_Ventas",
      rows: rows.map((r) => ({
        Fecha: r.fecha, Sucursal: sucName(r.sucursal_id),
        Usuario: r.usuario_email, Unidades: r.unidades,
        Total: r.total, Costo: r.total_costo, "Pollos Eq.": r.total_pollos_eq,
      })),
    });
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar venta?")) return;
    try { await api.delete(`/ventas/${id}`); toast.success("Eliminado"); cargar(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Consultas</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">{isAdmin ? "Historial de Ventas" : "Mis Ventas"}</h1>
        </div>
        <div className="flex gap-2">
            <Button variant="outline" onClick={expPDF} data-testid="hist-ventas-pdf"><FileDown className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" onClick={expXLS} data-testid="hist-ventas-xls"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
          </div>
      </div>

      <Card className="p-5 grid grid-cols-2 md:grid-cols-5 gap-3">
        <FilterField label="Desde"><Input type="date" value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} className="bg-blue-50 border-blue-200" /></FilterField>
        <FilterField label="Hasta"><Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="bg-blue-50 border-blue-200" /></FilterField>
        <FilterField label="Sucursal">
          <Select value={sucursalId || "all"} onValueChange={(v) => setSucursalId(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Producto">
          <Select value={productoId || "all"} onValueChange={(v) => setProductoId(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <div className="flex items-end">
          <Button onClick={cargar} className="bg-blue-600 hover:bg-blue-700 text-white w-full h-10" data-testid="hist-ventas-buscar">Filtrar</Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Fecha</Th><Th>Sucursal</Th><Th>Usuario</Th><Th right>Unidades</Th>
                <Th right>Pollos Eq.</Th><Th right>Total</Th>
                {puedeEditar && <Th>Acciones</Th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={puedeEditar ? 7 : 6} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={puedeEditar ? 7 : 6} className="px-4 py-8 text-center text-slate-500">Sin registros</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`hist-venta-${r.id}`}>
                  <Td>{r.fecha}</Td>
                  <Td>{sucName(r.sucursal_id)}</Td>
                  <Td className="text-xs text-slate-500">{r.usuario_email}</Td>
                  <Td right mono>{fmtNum(r.unidades, 0)}</Td>
                  <Td right mono>{fmtNum(r.total_pollos_eq, 1)}</Td>
                  <Td right mono className="font-semibold">{fmtMoney(r.total)}</Td>
                  {puedeEditar && (
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => setEditing(r)} data-testid={`hist-venta-edit-${r.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => eliminar(r.id)} className="text-red-600 hover:text-red-700" data-testid={`hist-venta-del-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-900 text-white">
                <tr>
                  <td className="px-4 py-3 text-sm font-semibold" colSpan={3}>TOTAL</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtNum(totalUnid, 0)}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg">{fmtMoney(totalVentas)}</td>
                  {puedeEditar && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Modal edicion (admin) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Venta — {editing?.fecha} · {sucName(editing?.sucursal_id)}</DialogTitle></DialogHeader>
          {editing && <EditVentaForm venta={editing} onSaved={() => { setEditing(null); cargar(); }} onCancel={() => setEditing(null)} sucursales={sucursales} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FilterField = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Th = ({ children, right }) => (
  <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${right ? "text-right" : ""}`}>{children}</th>
);
const Td = ({ children, right, mono, className = "" }) => (
  <td className={`px-4 py-2.5 text-sm ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
);

function EditVentaForm({ venta, onSaved, onCancel, sucursales }) {
  const [fecha, setFecha] = useState(venta.fecha);
  const [sucursalId, setSucursalId] = useState(venta.sucursal_id);
  const [detalle, setDetalle] = useState(venta.detalle.map((d) => ({ ...d })));

  const setCant = (i, val) => setDetalle((prev) => prev.map((d, idx) => idx === i ? { ...d, cantidad: parseFloat(val) || 0 } : d));
  const total = detalle.reduce((s, d) => s + d.cantidad * d.precio, 0);

  const guardar = async () => {
    try {
      await api.put(`/ventas/${venta.id}`, { fecha, sucursal_id: sucursalId, detalle });
      toast.success("Venta actualizada");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Fecha</Label><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="bg-blue-50 border-blue-200" /></div>
        <div>
          <Label>Sucursal</Label>
          <Select value={sucursalId} onValueChange={setSucursalId}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue /></SelectTrigger>
            <SelectContent>{sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="border rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr><th className="px-3 py-2 text-left">Producto</th><th className="px-3 py-2 text-right">Precio</th><th className="px-3 py-2 text-center w-28">Cantidad</th><th className="px-3 py-2 text-right">Subtotal</th></tr>
          </thead>
          <tbody>
            {detalle.map((d, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2">{d.nombre}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(d.precio)}</td>
                <td className="px-2 py-1 text-center"><Input type="number" min="0" step="any" value={d.cantidad} onChange={(e) => setCant(i, e.target.value)} className="bg-blue-50 border-blue-200 text-center font-mono h-8" /></td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(d.cantidad * d.precio)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-900 text-white">
            <tr><td colSpan={3} className="px-3 py-2 font-semibold">TOTAL</td><td className="px-3 py-2 text-right font-mono font-bold">{fmtMoney(total)}</td></tr>
          </tfoot>
        </table>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={guardar} className="bg-blue-600 hover:bg-blue-700 text-white">Guardar Cambios</Button>
      </DialogFooter>
    </div>
  );
}
